import type { CancelBookingFlowStep, CancelBookingOption, CancelBookingOptionEntry } from './types'
import type { FullCancellationSheetVariant, FullCancellationVariant } from '@/scopes/account/booking-details/pages/selectors/fullCancellationSelectors'

export const CANCEL_BOOKING_OPTION_REGISTRY: Record<CancelBookingOption, CancelBookingOptionEntry> = {
  'voucher': {
    labelKey: 'booking_details_cancel_booking_option_voucher_label',
  },
  'exchange': {
    labelKey: 'booking_details_cancel_booking_option_exchange_label',
  },
  'partial-cancellation': {
    labelKey: 'booking_details_cancel_booking_option_partial_cancellation_label',
    titleKey: 'booking_details_cancel_booking_partial_cancellation_sheet_title',
  },
  'full-cancellation': {
    labelKey: 'booking_details_cancel_booking_option_full_cancellation_label',
  },
}

export const DEFAULT_CANCEL_BOOKING_SHEET_TITLE_KEY = 'booking_details_cancel_booking_sheet_title'
export const DEFAULT_CANCEL_BOOKING_SHEET_CANCEL_BUTTON_LABEL_KEY = 'booking_details_cancel_booking_sheet_cancel_button_label'
export const DEFAULT_CANCEL_BOOKING_SHEET_CONFIRM_BUTTON_LABEL_KEY = 'booking_details_cancel_booking_sheet_confirm_button_label'

interface CancelBookingSheetLabelsConfig {
  titleKey?: string | null
  cancelButtonLabelKey?: string
  confirmButtonLabelKey?: string
}

export interface CancelBookingSheetLabels {
  titleKey: string | undefined
  cancelButtonLabelKey: string
  confirmButtonLabelKey: string
}

const FULL_CANCELLATION_SHEET_TITLE_KEY_BY_VARIANT: Record<FullCancellationSheetVariant, string> = {
  'free': 'booking_details_cancel_booking_full_cancellation_free_sheet_title',
  'with-cost': 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_title',
}

const FULL_CANCELLATION_SHEET_BUTTON_LABELS_BY_VARIANT: Record<
  FullCancellationSheetVariant,
  Pick<CancelBookingSheetLabels, 'cancelButtonLabelKey' | 'confirmButtonLabelKey'>
> = {
  'free': {
    cancelButtonLabelKey: 'booking_details_cancel_booking_full_cancellation_free_sheet_decline_button_label',
    confirmButtonLabelKey: 'booking_details_cancel_booking_full_cancellation_free_sheet_confirm_button_label',
  },
  'with-cost': {
    cancelButtonLabelKey: 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_decline_button_label',
    confirmButtonLabelKey: 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_confirm_button_label',
  },
}

const CANCEL_BOOKING_SHEET_LABELS_BY_STEP: Record<CancelBookingFlowStep, CancelBookingSheetLabelsConfig> = {
  'select-option': {},
  'exchange': {
    titleKey: 'booking_details_manage_booking_rebook_button_label',
  },
  'voucher-cancellation-confirmation': {
    titleKey: 'booking_details_cancel_booking_voucher_sheet_title',
    cancelButtonLabelKey: 'booking_details_cancel_booking_voucher_sheet_decline_button_label',
    confirmButtonLabelKey: 'booking_details_cancel_booking_voucher_sheet_confirm_button_label',
  },
  'partial-cancellation': {
    cancelButtonLabelKey: 'booking_details_cancel_booking_partial_cancellation_sheet_decline_button_label',
    confirmButtonLabelKey: 'booking_details_cancel_booking_partial_cancellation_sheet_continue_button_label',
  },
  'partial-cancellation-fee': {
    titleKey: null,
    confirmButtonLabelKey: 'booking_details_cancel_booking_partial_cancellation_fee_sheet_submit_button_label',
  },
  'full-cancellation-confirmation': {},
}

interface CancelBookingBodyBackButtonConfig {
  previousStep: CancelBookingFlowStep
  labelKey: string
}

const CANCEL_BOOKING_BODY_BACK_BUTTON_BY_STEP: Partial<Record<CancelBookingFlowStep, CancelBookingBodyBackButtonConfig>> = {
  'partial-cancellation-fee': {
    previousStep: 'partial-cancellation',
    labelKey: 'booking_details_cancel_booking_partial_cancellation_fee_reselect_participants_button_label',
  },
}

function resolveCancelBookingSheetTitleKey(
  step: CancelBookingFlowStep,
  stepConfig: CancelBookingSheetLabelsConfig,
): string | undefined {
  if (stepConfig.titleKey === null)
    return undefined

  if (stepConfig.titleKey !== undefined)
    return stepConfig.titleKey

  if (step === 'partial-cancellation')
    return CANCEL_BOOKING_OPTION_REGISTRY['partial-cancellation'].titleKey ?? DEFAULT_CANCEL_BOOKING_SHEET_TITLE_KEY

  return DEFAULT_CANCEL_BOOKING_SHEET_TITLE_KEY
}

export function getCancelBookingSheetLabels(
  step: CancelBookingFlowStep,
  fullCancellationVariant?: FullCancellationVariant | null,
  isSubmitting = false,
): CancelBookingSheetLabels {
  if (step === 'full-cancellation-confirmation') {
    const variant = fullCancellationVariant ?? 'free'

    return {
      titleKey: FULL_CANCELLATION_SHEET_TITLE_KEY_BY_VARIANT[variant],
      ...FULL_CANCELLATION_SHEET_BUTTON_LABELS_BY_VARIANT[variant],
    }
  }

  const stepConfig = CANCEL_BOOKING_SHEET_LABELS_BY_STEP[step]
  const confirmButtonLabelKey = step === 'voucher-cancellation-confirmation' && isSubmitting
    ? 'booking_details_cancel_booking_voucher_sheet_submitting_button_label'
    : stepConfig.confirmButtonLabelKey ?? DEFAULT_CANCEL_BOOKING_SHEET_CONFIRM_BUTTON_LABEL_KEY

  return {
    titleKey: resolveCancelBookingSheetTitleKey(step, stepConfig),
    cancelButtonLabelKey: stepConfig.cancelButtonLabelKey ?? DEFAULT_CANCEL_BOOKING_SHEET_CANCEL_BUTTON_LABEL_KEY,
    confirmButtonLabelKey,
  }
}

export function getCancelBookingBodyBackButtonConfig(step: CancelBookingFlowStep): CancelBookingBodyBackButtonConfig | undefined {
  return CANCEL_BOOKING_BODY_BACK_BUTTON_BY_STEP[step]
}
